'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { useSignUp } from '@clerk/nextjs'
import { isDisposableEmail } from '@/lib/disposable-emails'
import { desktopRedirectOrFallback } from './desktopRedirect'

export type SignUpStep = 'credentials' | 'verification'

interface SignUpFormState {
  firstName: string
  lastName: string
  email: string
  password: string
  isLoading: boolean
  error: string | null
  step: SignUpStep
}

const SIGN_UP_REDIRECT = process.env.NEXT_PUBLIC_CLERK_SIGN_UP_FALLBACK_REDIRECT_URL || '/send'

export function useSignUpForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { signUp, errors, fetchStatus } = useSignUp()

  function redirectAfterSignUp() {
    const target = desktopRedirectOrFallback(new URLSearchParams(searchParams.toString()), SIGN_UP_REDIRECT)
    if (target.startsWith('/api/')) {
      window.location.href = target
    } else {
      router.push(target)
    }
  }

  const [state, setState] = useState<SignUpFormState>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    isLoading: false,
    error: null,
    step: 'credentials'
  })

  // Reflect Clerk field errors into local error string
  const clerkError =
    errors.fields.emailAddress?.code ??
    errors.fields.password?.code ??
    errors.fields.firstName?.code ??
    errors.fields.lastName?.code ??
    errors.fields.code?.code ??
    errors.global?.[0]?.code ??
    null

  function setField(field: 'firstName' | 'lastName' | 'email' | 'password', value: string) {
    setState((prev) => ({ ...prev, [field]: value, error: null }))
  }

  async function handleSubmit() {
    if (fetchStatus === 'fetching' || !signUp) return

    // Reject disposable-inbox domains before hitting Clerk (spec §7.1). This is
    // the code-side gate + immediate UX feedback; the authoritative block is
    // Clerk's dashboard "block disposable email domains" toggle.
    if (isDisposableEmail(state.email)) {
      setState((prev) => ({ ...prev, isLoading: false, error: 'disposable_email' }))
      return
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    // Step 1: create sign-up with identifying info
    const { error: createError } = await signUp.create({
      emailAddress: state.email,
      firstName: state.firstName,
      lastName: state.lastName
    })
    if (createError) {
      setState((prev) => ({ ...prev, isLoading: false, error: createError.code }))
      return
    }

    // Step 2: submit password
    const { error: pwError } = await signUp.password({
      emailAddress: state.email,
      password: state.password
    })
    if (pwError) {
      setState((prev) => ({ ...prev, isLoading: false, error: pwError.code }))
      return
    }

    // Step 3: send email verification code
    const { error: codeError } = await signUp.verifications.sendEmailCode()
    if (codeError) {
      setState((prev) => ({ ...prev, isLoading: false, error: codeError.code }))
      return
    }

    setState((prev) => ({ ...prev, isLoading: false, step: 'verification' }))
  }

  async function submitCode(code: string) {
    if (fetchStatus === 'fetching' || !signUp) return
    setState((prev) => ({ ...prev, isLoading: true, error: null }))

    const { error: verifyError } = await signUp.verifications.verifyEmailCode({ code })
    if (verifyError) {
      setState((prev) => ({ ...prev, isLoading: false, error: verifyError.code }))
      return
    }

    if (signUp.status === 'complete') {
      const { error: finalizeError } = await signUp.finalize()
      if (finalizeError) {
        setState((prev) => ({ ...prev, isLoading: false, error: finalizeError.code }))
        return
      }
      redirectAfterSignUp()
      return
    }
    setState((prev) => ({ ...prev, isLoading: false }))
  }

  async function resendCode() {
    if (!signUp) return
    // best-effort resend
    await signUp.verifications.sendEmailCode().catch(() => undefined)
  }

  function goBack() {
    setState((prev) => ({ ...prev, step: 'credentials', error: null }))
  }

  const error = state.error ?? clerkError

  return {
    firstName: state.firstName,
    lastName: state.lastName,
    email: state.email,
    password: state.password,
    isLoading: state.isLoading,
    error,
    step: state.step,
    setField,
    handleSubmit,
    submitCode,
    resendCode,
    goBack
  }
}
